import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

const questions = [
  { key: "msme_category", label: "Enterprise category", type: "select", options: ["Micro", "Small", "Medium"] },
  { key: "pan_available", label: "Is PAN available?", type: "bool" },
  { key: "udyam_available", label: "Is Udyam available?", type: "bool" },
  { key: "activity_eligible", label: "Is the activity generally eligible (non-farm, non-retail)?", type: "bool" },
  { key: "participating_lender", label: "Is the chosen lender a CGTMSE member institution?", type: "bool" },
  { key: "collateral_offered", label: "Is collateral being offered?", type: "bool" },
  { key: "third_party_guarantee", label: "Is a third-party guarantee proposed?", type: "bool" },
  { key: "facility_type", label: "Facility type", type: "select", options: ["Term Loan", "Working Capital", "Composite", "Other"] },
  { key: "requested_amount", label: "Requested amount (₹)", type: "number" },
  { key: "enterprise_type", label: "Enterprise type", type: "select", options: ["new", "existing"] },
  { key: "existing_facilities", label: "Any existing credit facilities?", type: "bool" },
  { key: "lender_willingness", label: "Has the lender indicated willingness?", type: "bool" },
];

export default function CGTMSEInfo() {
  const [answers, setAnswers] = useState({
    msme_category: "Micro", pan_available: true, udyam_available: true, activity_eligible: true,
    participating_lender: true, collateral_offered: false, third_party_guarantee: false,
    facility_type: "Term Loan", requested_amount: 1000000, enterprise_type: "existing",
    existing_facilities: false, lender_willingness: true,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/cgtmse/assess", answers);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Assessment failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Preliminary Guidance</div>
      <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 brand-heading" data-testid="cgtmse-heading">CGTMSE preliminary check</h1>
      <p className="mt-3 text-slate-600 max-w-3xl">CGTMSE provides guarantee cover to member lending institutions for qualifying credit extended without collateral / third-party guarantee to micro & small enterprises. This preliminary check helps you gauge suitability. It is not a sanction and not a guarantee approval.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <h2 className="font-semibold text-slate-900 brand-heading text-lg">Questions</h2>
          <div className="mt-5 space-y-4">
            {questions.map((q) => (
              <div key={q.key} className="text-sm">
                <label className="block font-medium text-slate-800 mb-1">{q.label}</label>
                {q.type === "bool" ? (
                  <div className="flex items-center gap-4">
                    {[true, false].map((v) => (
                      <label key={String(v)} className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={q.key} checked={answers[q.key] === v} onChange={() => setAnswers({ ...answers, [q.key]: v })} data-testid={`cgtmse-${q.key}-${v}`} />
                        <span>{v ? "Yes" : "No"}</span>
                      </label>
                    ))}
                  </div>
                ) : q.type === "select" ? (
                  <select
                    className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                    value={answers[q.key]}
                    onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                    data-testid={`cgtmse-${q.key}`}
                  >
                    {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type="number"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                    value={answers[q.key]}
                    onChange={(e) => setAnswers({ ...answers, [q.key]: Number(e.target.value) })}
                    data-testid={`cgtmse-${q.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <button
            onClick={submit}
            disabled={loading}
            data-testid="cgtmse-assess-btn"
            className="mt-6 inline-flex rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            {loading ? "Assessing…" : "Run preliminary check"}
          </button>
        </div>

        <div className="space-y-6">
          {result ? (
            <div className="p-6 rounded-lg border border-slate-200 bg-white" data-testid="cgtmse-result">
              <div className="text-xs uppercase tracking-widest text-slate-500">Outcome</div>
              <div className="text-xl font-bold text-slate-900 brand-heading mt-1">{result.outcome}</div>
              <div className="mt-4">
                <div className="text-sm font-semibold text-emerald-700 mb-1">Positive factors</div>
                <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                  {(result.positive_factors || []).map((r) => <li key={r}>{r}</li>)}
                </ul>
              </div>
              {result.concerns?.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-orange-700 mb-1">Concerns</div>
                  <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                    {result.concerns.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              )}
              {result.followups?.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-700 mb-1">Follow-ups</div>
                  <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                    {result.followups.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-600 text-sm">
              Run the check to see indicative guidance.
            </div>
          )}
          <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900 leading-relaxed">
            CGTMSE does not directly provide loans to borrowers. Guarantee cover does not ensure loan sanction. The participating lender decides whether to apply for guarantee cover. Applicable fees, coverage and conditions depend on prevailing scheme rules.
          </div>
        </div>
      </div>

      <div className="mt-12"><DisclaimerBanner /></div>
    </div>
  );
}
